import type { ActionTree, ActionContext, Module, MutationTree } from 'vuex';
import { ClubModuleConstant, StorageConstant } from '../../constants';
import { urlService } from '../../services/urlService';
import { clubService } from '../../services/clubService';
import { nearestClubService } from '../../services/nearestClubService';
import { storageService } from '@life-time-inc/enterprise-framework-core';
import type { ltFramework } from '@life-time-inc/enterprise-framework-core';

interface IClubState {
	clubDetails: ltFramework.IClubDetails;
	initializing: boolean;
}

const ClubState: IClubState = {
	clubDetails: {
		club: {} as ltFramework.IClubData,
		initialized: false
	},
	initializing: false
};

const { action, mutation } = ClubModuleConstant;

const ClubMutations: MutationTree<IClubState> = {
	[mutation.setClubDetails](state: IClubState, payload: ltFramework.IClubDetails) {
		state.clubDetails = payload;
	},

	[mutation.setInitializing](state: IClubState, initializing: boolean) {
		state.initializing = initializing;
	}
};

export const ClubActions: ActionTree<IClubState, ltFramework.IRootState> = {
	async [action.initialize]({
		commit,
		state
	}: ActionContext<IClubState, ltFramework.IRootState>): Promise<void> {
		if (state.initializing) {
			return;
		}

		commit(mutation.setInitializing, true);

		try {
			let club: ltFramework.IClubData = {} as ltFramework.IClubData;
			const windowLtClub: ltFramework.IClubDetailsFromDOM =
				window.lt?.club || ({} as ltFramework.IClubDetailsFromDOM);
			const hasWindowLtClub = Boolean(windowLtClub?.mmsClubId);

			const pageProperties: ltFramework.IPageProperties =
				window.lt?.pageProperties || ({} as ltFramework.IPageProperties);

			const recentClubs: ltFramework.IRecentClub[] = storageService.localGet(
				StorageConstant.recentClubs
			) as ltFramework.IRecentClub[];
			const recentClubId = Number(recentClubs?.[0]?.id);
			const hasRecentClub = !isNaN(recentClubId);

			const queryParams = new URLSearchParams(window.location.search);
			const queryParamClubId = Number(
				queryParams.get('club') || queryParams.get('clubId') || undefined
			);
			const hasClubQueryParam = !isNaN(queryParamClubId);

			// add alertnotification calls

			if (hasWindowLtClub) {
				club = copyClubData(windowLtClub, pageProperties);
				club.pages = { ...windowLtClub.clubPagePaths };
			} else {
				let response;

				if (hasClubQueryParam) {
					response = await clubService.getClubById(queryParamClubId);
				} else if (hasRecentClub) {
					response = await clubService.getClubById(recentClubId);
				} else {
					response = await nearestClubService.getNearestClub();
				}
				club = copyClubData(response.data[0], pageProperties);
			}

			if (!club.isClubPage) {
				club.navHtml = await getClubNav(club.link);
			}

			commit(mutation.setClubDetails, { club, initialized: true });
		} finally {
			commit(mutation.setInitializing, false);
		}
	},

	[action.initializeClub](
		{ commit, state }: ActionContext<IClubState, ltFramework.IRootState>,
		inputClub: ltFramework.IClubData
	): Promise<void> {
		if (state.clubDetails.initialized) {
			return Promise.resolve();
		}

		let club: ltFramework.IClubData = {} as ltFramework.IClubData;
		const pageProperties: ltFramework.IPageProperties =
			window.lt?.pageProperties || ({} as ltFramework.IPageProperties);

		if (window.lt?.club) {
			club = copyClubData(window.lt.club, pageProperties);
			club.pages = { ...window.lt.club.clubPagePaths };
			club.validPageKeys = Object.keys(club.pages);
			commit(mutation.setClubDetails, { club, initialized: true });
		} else if (inputClub) {
			club = inputClub;
			commit(mutation.setClubDetails, { club, initialized: true });
		}

		return Promise.resolve();
	},

	[action.updateClub](
		{ commit }: ActionContext<IClubState, ltFramework.IRootState>,
		inputClub: ltFramework.IClubData
	): Promise<void> {
		commit(mutation.setClubDetails, { club: inputClub, initialized: true });

		return Promise.resolve();
	}
};

export const ClubModule: Module<IClubState, ltFramework.IRootState> = {
	namespaced: true,
	state: ClubState,
	actions: ClubActions,
	mutations: ClubMutations
};

function copyClubData(
	input: ltFramework.IClubDetailsFromDOM | ltFramework.IClubFromService,
	pageProperties: ltFramework.IPageProperties
): ltFramework.IClubData {
	const club: ltFramework.IClubData = {} as ltFramework.IClubData;

	club.country = input.country || '';
	club.id = input.mmsClubId;
	club.clubTypeId = input.clubTypeId;
	club.domainPrefix = input.domainPrefix || '';
	club.street = input.street1 || '';
	club.city = input.city || '';
	club.state = input.stateAbbr || '';
	club.stateName = input.state || '';
	club.zip = input.zip || '';
	club.region = input.region || '';
	club.area = input.area || '';
	club.timeZone = input.timeZone || '';
	club.membershipLevel = input.membershipLevel || '';
	club.juniorAccess = input.juniorAccess || '';
	club.status = input.open || '';
	club.openDate = input.openDate || '';
	club.latitude = input.latitude;
	club.longitude = input.longitude;
	club.name =
		(input as ltFramework.IClubDetailsFromDOM).marketingClubName ||
		(input as ltFramework.IClubFromService).name ||
		'';
	club.description = input.comingSoon || '';
	club.phone = input.phone || '';
	club.link = input.clubPagePath || window.location.href;
	club.prospectLink = input.prospectPagePath || window.location.href;

	club.pages = urlService.buildPagePaths({ ...input.clubPagePaths });
	club.validPageKeys = Object.keys(club.pages);

	club.isClubPage = Boolean(pageProperties?.club);

	return club;
}

async function getClubNav(clubLink: string): Promise<string | undefined> {
	if (!clubLink) {
		return;
	}

	//remove the .html from the link
	if (clubLink.indexOf('.html') >= 0) {
		clubLink = clubLink.substring(0, clubLink.indexOf('.html'));
	}

	clubLink = window.location.pathname.includes('content')
		? clubLink.replace('/content/lt/en_us', '')
		: clubLink;

	// create link to header
	clubLink = clubLink + '/_jcr_content/header.html';
	try {
		const response = await fetch(clubLink);
		if (!response.ok) {
			return;
		}
		return await response.text();
	} catch {
		return;
	}
}
